/**
 * تبلیغِ وب در /play — مدیااد (وبِ تپسل)، آینه‌ی تبلیغِ پیش از میزِ اپ.
 *
 * تصمیمِ کاربر (۱۹ اکتبر ۲۰۲۶): «همان تبلیغِ اپ روی سایت هم». وبِ تپسل فقط ویجتِ
 * بنری/همسان دارد — نه ویدیوی میان‌پرده، نه جایزه‌دار، و نه هیچ رویدادِ «بارگیری
 * شد / تبلیغی نبود» — پس:
 *
 * - **کارتِ مکث** جای میان‌پرده: پیش از صفِ بازیِ سریع، میزِ ربات و جامِ آرنا
 *   یک ویجت روی صفحه می‌آید و بعد از پنج ثانیه «شروعِ بازی» باز می‌شود. اتاقِ
 *   دوستان هرگز (شروعِ میزبان همه را با هم می‌کشد، مثلِ اپ).
 * - جایزه‌دار نیست: وب هیچ‌کدام از جایزه‌های اپ (دوبرابرِ سکه، هدیه، ادامه) را ندارد.
 * - سقف‌ها همان کلیدِ `ads` و همان معنا: روزهای اولِ «نصب» (اینجا اولین بازدیدِ این
 *   مرورگر)، میزهای اولِ هر بار بازکردنِ صفحه، فاصله، سقفِ روزانه، و مشترک هرگز.
 * - از ۲۱ اکتبر ۲۰۲۶ (تصمیمِ کاربر: «درآمدِ بیشتر، مزاحمتِ کمتر») کارت بر پایه‌ی **زمانِ
 *   بازی** است: `play_minutes` دقیقه سرِ میز (نه در لابی، نه در زبانه‌ی پنهان) از کارتِ
 *   قبلی، و بعد از `loss_streak` باختِ پشتِ هم نه. پس کسی که منچِ سه‌دقیقه‌ای بازی می‌کند
 *   هر میز کارت نمی‌بیند، و کسی که ساعت‌ها بازی می‌کند سقفِ روزانه‌ی بالاتری دارد.
 * - **آگهیِ پایانِ میز** (`web_result`): روی پنلِ نتیجه‌ی مسابقه، بی انتظار و بی شمارش —
 *   ui/Ads.tsx، [[ResultAd]].
 * - **تماشا هر بار** (۲۳ اکتبر ۲۰۲۶، کاربر: «قبل از تماشای بازیِ آنلاین باید تبلیغ پخش بشه»):
 *   پیش از تماشای میز کارت همیشه باز می‌شود — نه زمانِ بازی، نه فاصله، نه سقفِ روزانه، نه
 *   روزهای اول و میزِ اول و رشته‌ی باخت. فقط مشترک و کلیدِ خاموشِ `web_on_watch` جلویش را
 *   می‌گیرند. تماشاچی دستی ندارد که از دست برود.
 *
 * چون بارگیری را نمی‌شود پیش از نشان‌دادن فهمید (ویجتِ پنهان یعنی نمایشِ
 * دیده‌نشده و شکستنِ قاعده‌ی شبکه)، کارت اول خالی باز می‌شود و اگر تا
 * {@link FILL_MS} چیزی در ویجت ننشست، بسته می‌شود و میز همان لحظه راه می‌افتد.
 * انتظارِ بیشتری هرگز نیست.
 */
import { json } from "./remote";
import { signal } from "./signal";

/** مقدارِ کلیدِ `ads` که وب می‌خوانَد. سرور همین را پیش از انتشار سنجیده است. */
export type AdsKey = {
  on?: boolean;
  web_domain?: string;
  web_break?: string;
  web_lobby?: string;
  web_guide?: string;
  web_game?: string[];
  web_result?: string;
  install_days?: number;
  gap_minutes?: number;
  per_day?: number;
  skip_first?: number;
  play_minutes?: number;
  loss_streak?: number;
  web_on_quick?: boolean;
  web_on_bots?: boolean;
  web_on_cup?: boolean;
  web_on_watch?: boolean;
  web_on_lobby?: boolean;
  web_on_result?: boolean;
  web_hold_seconds?: number;
  web_fill_ms?: number;
  web_lobby_slots?: number;
};

/** پیش از چه میزی کارتِ مکث باز می‌شود — هر کدام کلیدِ خودش را در پنل دارد (`web_on_*`). */
export type Place = "quick" | "bots" | "cup" | "watch";

export type WebAds = {
  domain: string;
  /** ویجتِ کارتِ مکث؛ خالی = بی کارت. */
  brk: string;
  /** ویجتِ لابی؛ خالی = بی بنر. */
  lobby: string;
  /** چند جای بنرِ لابی در /play پر شود (۱ تا ۳) — [[LobbyAd]]. */
  lobbySlots: number;
  /** ویجتِ پنلِ پایانِ میز؛ خالی = بی آگهی. */
  result: string;
  installDays: number;
  gapMs: number;
  perDay: number;
  skipFirst: number;
  /** زمانِ بازیِ لازم از کارتِ قبلی؛ صفر = فقط {@link gapMs}. */
  playMs: number;
  /** این‌همه باختِ پشتِ هم = بی کارت تا برد؛ صفر = خاموش. */
  lossStreak: number;
  /** کارتِ مکث پیش از هر کدام روشن است؟ */
  places: Record<Place, boolean>;
  /** «شروعِ بازی» این‌قدر پس از پرشدن باز می‌شود. */
  holdMs: number;
  /** کارتی که تا این‌قدر پر نشد بسته می‌شود. */
  fillMs: number;
};

const DOMAIN_RE = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
const TAG_RE = /^[A-Za-z0-9_-]{1,200}$/;

const int = (v: unknown, dflt: number, lo: number, hi: number): number =>
  typeof v === "number" && Number.isInteger(v) && v >= lo && v <= hi ? v : dflt;

/**
 * کلید → تنظیمِ وب؛ null یعنی وب بی تبلیغ. پیش‌فرض‌های سقف همان تصمیمِ کاربر
 * (Backend/internal/ads، AdCaps.cs). شکلِ نادرست هم null است و نه نیمه‌روشن:
 * نشانیِ لودر از همین دامنه ساخته می‌شود.
 */
export function readAds(v: AdsKey | null | undefined): WebAds | null {
  if (!v || v.on !== true) return null;
  const domain = typeof v.web_domain === "string" ? v.web_domain : "";
  if (!DOMAIN_RE.test(domain)) return null;
  const tag = (t: unknown) => (typeof t === "string" && TAG_RE.test(t) ? t : "");
  // خاموش در پنل = همان «بی ویجت»؛ شناسه در کلید می‌ماند تا روشن‌کردنِ دوباره یک کلیک باشد.
  const on = (b: unknown) => b !== false;
  const brk = tag(v.web_break);
  const lobby = on(v.web_on_lobby) ? tag(v.web_lobby) : "";
  const result = on(v.web_on_result) ? tag(v.web_result) : "";
  if (!brk && !lobby && !result) return null;
  return {
    domain, brk, lobby: lobby === brk ? "" : lobby, result,
    // پیش‌فرض یک بنر (کاربر: «در وب یک نمونه آگهی کافی است»)؛ بیشتر از پنل
    lobbySlots: int(v.web_lobby_slots, 1, 1, 3),
    installDays: int(v.install_days, 3, 0, 60),
    gapMs: int(v.gap_minutes, 4, 1, 240) * 60_000,
    perDay: int(v.per_day, 10, 0, 12),
    skipFirst: int(v.skip_first, 1, 0, 10),
    playMs: int(v.play_minutes, 6, 0, 60) * 60_000,
    lossStreak: int(v.loss_streak, 2, 0, 10),
    places: { quick: on(v.web_on_quick), bots: on(v.web_on_bots), cup: on(v.web_on_cup), watch: on(v.web_on_watch) },
    holdMs: int(v.web_hold_seconds, 5, 0, 30) * 1000,
    fillMs: int(v.web_fill_ms, 1500, 300, 10_000),
  };
}

/**
 * تنظیمِ جاری. پس از رسیدنِ پیکربندی ({@link initWebAds}) پر می‌شود؛ تا آن
 * وقت null و یعنی «بی تبلیغ» — نه انتظار.
 */
export const ads = signal<WebAds | null>(null);

// ───────────────────────── سیاست ─────────────────────────

/** چرا کارت نیامد — همان نام‌های AdBlock ِ اپ. */
export type Block = "" | "subscriber" | "new" | "first" | "loss" | "daily" | "play" | "soon";

/** نامِ همان دلیل در اپ (AdBlock) — تا آمارِ پنل اپ و وب را یک‌جا بشمارد. */
const BLOCK_NAME: Record<Exclude<Block, "">, string> = {
  subscriber: "Subscriber", new: "NewInstall", first: "FirstGame", loss: "LossStreak",
  daily: "DailyCap", play: "NotEnoughPlay", soon: "TooSoon",
};

// ───────────────────────── رویدادها ─────────────────────────

/**
 * رویدادهای آگهیِ وب برای صفحه‌ی «تبلیغات» ِ پنل — همان نام‌ها و جاهای اپ (`ad_start`،
 * `ad_skip_policy`، …) با `src=web`: WB کارتِ مکث، WR پایانِ میز، WL لابی. فرستادن کارِ ui/Ads.tsx
 * است ({@link setAdSender}) تا این پرونده به client وابسته نشود؛ دسته‌ای و بی‌صدا — آمار است، نه
 * حسابداری، و هرگز جلوی بازی را نمی‌گیرد.
 */
export type AdEvent = { name: string; props: Record<string, string>; client_ms: number };

let sender: ((evs: AdEvent[]) => void) | null = null;
let queue: AdEvent[] = [];
let flushT: ReturnType<typeof setTimeout> | null = null;

export function setAdSender(fn: ((evs: AdEvent[]) => void) | null): void { sender = fn; }

export function adEvent(name: string, p: string, props: Record<string, string> = {}): void {
  queue.push({ name, props: { p, src: "web", ...props }, client_ms: Date.now() });
  if (queue.length > 50) queue = queue.slice(-50);
  if (flushT === null) flushT = setTimeout(flushAds, 4000);
}

export function flushAds(): void {
  if (flushT !== null) { clearTimeout(flushT); flushT = null; }
  if (!queue.length || !sender) return;
  const evs = queue;
  queue = [];
  try { sender(evs); } catch { /* آمار است */ }
}

/**
 * `play`: میلی‌ثانیه‌ی سرِ میز از کارتِ قبلی (از چند بازدید جمع می‌شود). `losses`: باخت‌های
 * پشتِ همِ همین الان.
 */
type Book = { first: string; day: string; today: number; last: number; play: number; losses: number };

/** یک تیکِ ساعتِ میز بیش از این نمی‌شمارد — خوابِ لپ‌تاپ یا زبانه‌ی یخ‌زده ساعت‌ها نیست. */
export const MAX_TICK_MS = 15_000;

const KEY = "bazivan.ads";

/** تاریخِ محلی، «۲۰۲۶-۱۰-۱۹». روزِ تقویم و نه ۲۴ ساعت، مثلِ سقفِ روزانه‌ی اپ. */
const dayOf = (ms: number): string => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const daysBetween = (a: string, b: string): number =>
  Math.round((Date.parse(b + "T12:00:00") - Date.parse(a + "T12:00:00")) / 86_400_000);

/**
 * دفترِ سقف‌ها. در localStorage، چون «روزهای اولِ نصب» و «سقفِ روزانه» از یک
 * بار بازکردن بیشتر عمر دارند. مرورگرِ بی ذخیره (حالتِ ناشناس، ذخیره‌ی بسته)
 * دفترِ تازه می‌گیرد — یعنی همیشه «روزِ اول» و بی کارت، که سمتِ امن است.
 */
export class AdBook {
  private tables = 0;
  private mem: Book | null = null;

  constructor(private now: () => number = Date.now, private store: Storage | null = safeStore()) {}

  private read(): Book {
    const today = dayOf(this.now());
    let b: Book | null = this.mem;
    if (!b && this.store) {
      try {
        const raw = JSON.parse(this.store.getItem(KEY) ?? "null") as Partial<Book> | null;
        if (raw && typeof raw.first === "string") {
          b = {
            first: raw.first, day: String(raw.day ?? today), today: Number(raw.today) || 0, last: Number(raw.last) || 0,
            play: Math.max(0, Number(raw.play) || 0), losses: Math.max(0, Number(raw.losses) || 0),
          };
        }
      } catch { /* خراب: از نو */ }
    }
    if (!b) b = { first: today, day: today, today: 0, last: 0, play: 0, losses: 0 };
    if (b.day !== today) b = { ...b, day: today, today: 0 };
    this.mem = b;
    return b;
  }

  private write(b: Book): void {
    this.mem = b;
    try { this.store?.setItem(KEY, JSON.stringify(b)); } catch { /* پُر یا بسته */ }
  }

  /**
   * همان CheckPreGame ِ اپ، به همان ترتیب: از بنیادی‌ترین مانع به موقتی‌ترین.
   * «باخت» پیش از «سقفِ روزانه» است چون تا برد ادامه دارد؛ «زمانِ بازی» پیش از «فاصله»،
   * چون فاصله‌ی ساعتِ دیواری فقط کفِ آن است.
   */
  check(a: WebAds, subscriber: boolean): Block {
    const b = this.read();
    this.write(b);   // اولین بازدید همین حالا ثبت شود، نه اولین کارت
    if (subscriber) return "subscriber";
    if (daysBetween(b.first, b.day) < a.installDays) return "new";
    if (this.tables < a.skipFirst) return "first";
    if (a.lossStreak > 0 && b.losses >= a.lossStreak) return "loss";
    if (b.today >= a.perDay) return "daily";
    if (b.play < a.playMs) return "play";
    if (b.last > 0 && this.now() - b.last < a.gapMs) return "soon";
    return "";
  }

  /** زمانِ سرِ میز (بازی یا تماشا). هر تیک حداکثر {@link MAX_TICK_MS}. */
  notePlay(ms: number): void {
    if (!(ms > 0)) return;
    const b = this.read();
    this.write({ ...b, play: b.play + Math.min(ms, MAX_TICK_MS) });
  }

  /** نتیجه‌ی مسابقه‌ی خودم: باخت یکی به رشته اضافه می‌کند، برد یا مساوی صفرش می‌کند. */
  noteResult(won: boolean | null): void {
    const b = this.read();
    this.write({ ...b, losses: won === false ? b.losses + 1 : 0 });
  }

  /** زمانِ بازیِ جمع‌شده از کارتِ قبلی (برای آزمون). */
  get played(): number { return this.read().play; }

  /** باخت‌های پشتِ همِ همین الان (برای آزمون). */
  get lossRun(): number { return this.read().losses; }

  /** اولین بازدید را ثبت کن (سرِ بالا آمدن، نه سرِ اولین کارت). */
  touch(): void { this.write(this.read()); }

  /** میزی از منو شروع شد — با کارت یا بی کارت. پس از {@link check}. */
  noteTable(): void { this.tables++; }

  /** کارت واقعاً تبلیغ نشان داد. فقط این شمرده می‌شود، نه کارتِ خالی؛ و زمانِ بازی از صفر. */
  noteShown(): void {
    const b = this.read();
    this.write({ ...b, today: b.today + 1, last: this.now(), play: 0 });
  }
}

function safeStore(): Storage | null {
  try { return typeof localStorage === "undefined" ? null : localStorage; } catch { return null; }
}

export const book = new AdBook();

// ───────────────────────── قابِ آگهی ─────────────────────────

/**
 * مدیااد فقط در قابِ `/ad.html` اجرا می‌شود (public/ad.html)، نه روی این صفحه.
 *
 * تا ۲۱ اکتبر ۲۰۲۶ لودرش همین‌جا بود: هم‌ریشه با localStorage (شناسه‌ی دستگاه، توکنِ پنلِ
 * ادمین) و با توکنِ بازیکن در حافظه و سرآیندها. قاب sandbox است بی allow-same-origin، پس
 * ریشه‌اش «بی‌نام» است و از این صفحه هیچ نمی‌بیند. به این‌جا فقط ارتفاع و «لودر بالا آمد»
 * می‌رسد ({@link readAdMessage}).
 *
 * allow-popups-to-escape-sandbox: کلیک روی آگهی سایتِ آگهی‌دهنده را بیرون از sandbox
 * باز می‌کند، وگرنه همان سایت هم شکسته باز می‌شد.
 */
export const AD_SANDBOX = "allow-scripts allow-popups allow-popups-to-escape-sandbox";

/** نشانیِ قاب. بی tag فقط لودر — گرم‌کردن، بی هیچ نمایشی. */
export function adFrameSrc(domain: string, tag = ""): string {
  const q = new URLSearchParams({ d: domain });
  if (tag) q.set("t", tag);
  return "/ad.html?" + q.toString();
}

/** `w`: پهنای بنرِ ثابتی که از قاب باریک‌تر است (صفر = تمام‌پهنا) — قاب به همان جمع و وسط می‌شود. */
export type AdMessage = { h: number; ready: boolean; w: number };

/**
 * پیامِ قاب، فقط اگر از همان قاب آمده و شکلش درست است. ریشه‌ی قاب «null» است، پس
 * فرستنده با `source` شناخته می‌شود و نه `origin`.
 */
export function readAdMessage(e: MessageEvent, from: Window | null | undefined): AdMessage | null {
  if (!from || e.source !== from) return null;
  const d = e.data as { bazivanAd?: unknown; h?: unknown; ready?: unknown; w?: unknown } | null;
  if (!d || d.bazivanAd !== 1 || typeof d.h !== "number" || !Number.isFinite(d.h)) return null;
  const w = typeof d.w === "number" && Number.isFinite(d.w) ? Math.max(0, Math.min(1200, Math.round(d.w))) : 0;
  return { h: Math.max(0, Math.min(1200, Math.round(d.h))), ready: d.ready === true, w };
}

let warm: { domain: string; up: boolean } | null = null;

/**
 * قابِ پنهانِ لودر، یک بار در هر صفحه: کارتِ مکث وقتی باز می‌شود که لودر بالا آمده
 * باشد (مسدودکننده‌ی تبلیغ یعنی میز بی انتظار)، و لودرِ کش‌شده قابِ کارت را زودتر پر
 * می‌کند. بی ویجت چیزی نشان نمی‌دهد — همان «لودرِ بی ویجت» ِ پیش از قاب.
 */
export function ensureLoader(domain: string): void {
  if (warm?.domain === domain || typeof document === "undefined" || !document.body) return;
  const w = { domain, up: false };
  warm = w;
  const f = document.createElement("iframe");
  f.hidden = true;
  f.title = "";
  f.setAttribute("aria-hidden", "true");
  f.setAttribute("sandbox", AD_SANDBOX);
  f.src = adFrameSrc(domain);
  window.addEventListener("message", (e) => {
    const m = readAdMessage(e, f.contentWindow);
    if (m?.ready) w.up = true;
  });
  document.body.appendChild(f);
}

/** لودر در قاب بالا آمده؟ نه = مسدودکننده‌ی تبلیغ، شبکه، یا هنوز در راه. */
export const loaderReady = (): boolean => warm?.up === true;

/** فقط برای آزمون: لودرِ آماده یا نه، بی قابِ واقعی. */
export function __setLoaderReady(up: boolean, domain = "bazivan.com"): void {
  warm = { domain, up };
}

// ───────────────────────── کارتِ مکث ─────────────────────────

/** کارتِ باز. `go` میز را راه می‌اندازد — دقیقاً یک بار. */
export type Break = { id: number; domain: string; tag: string; place: Place; holdMs: number; fillMs: number; go: () => void };

export const adBreak = signal<Break | null>(null);

/** مهلتِ پرشدنِ ویجت پس از باز شدنِ کارت — پیش‌فرض؛ پنل `web_fill_ms` را عوض می‌کند. */
export const FILL_MS = 1500;
/** پس از پرشدن، «شروعِ بازی» این‌قدر بعد باز می‌شود (تصمیمِ کاربر: پنج ثانیه) — پیش‌فرضِ
 *  `web_hold_seconds`. */
export const HOLD_MS = 5000;
/**
 * مهلتِ پرشدنِ کارتِ تماشا وقتی لودرِ گرم هنوز بالا نیامده — کفِ `web_fill_ms`. لینکِ «تماشا» ِ
 * سایت (‎/play?watch=<id>) صفحه را تازه باز می‌کند و میز همین که وصل شدیم شروع می‌شود، پیش از
 * آن‌که لودر برسد؛ قابِ خودِ کارت لودر و ویجت را با هم می‌آورد و ۱٫۵ ثانیه برایش کم است.
 * مسدودکننده‌ی تبلیغ هم بیش از این منتظر نمی‌ماند.
 */
export const COLD_FILL_MS = 4000;

let seq = 0;

/**
 * پیش از میزی که از منو شروع می‌شود؛ بعد `start` — همیشه، دقیقاً یک بار.
 *
 * لمسِ دوم وقتی کارت باز است نادیده گرفته می‌شود (وگرنه دو صف پشتِ هم). اگر
 * قاعده اجازه نداد یا لودر بالا نیامده بود، میز همان لحظه شروع می‌شود.
 *
 * تماشا جداست: قاعده‌های آهنگ را نمی‌پرسد و با لودرِ سرد هم کارت باز می‌شود، با مهلتِ
 * {@link COLD_FILL_MS} — بالای همین پرونده.
 */
export function beforeTable(start: () => void, subscriber: boolean, a: WebAds | null = ads.value, place: Place = "quick"): void {
  if (adBreak.value) return;
  viaRoom = false;
  if (!a || !a.brk) { start(); return; }
  if (!a.places[place]) {
    book.noteTable();
    adEvent("ad_skip_policy", "WB", { why: "PlaceOff", at: place });
    start();
    return;
  }
  ensureLoader(a.domain);
  const watch = place === "watch";
  const block: Block = !watch ? book.check(a, subscriber) : subscriber ? "subscriber" : "";
  book.noteTable();
  if (block) { adEvent("ad_skip_policy", "WB", { why: BLOCK_NAME[block], at: place }); start(); return; }
  const cold = !loaderReady();
  if (cold && !watch) { adEvent("ad_fail", "WB", { why: "not_ready", at: place }); start(); return; }
  let done = false;
  adBreak.value = {
    id: ++seq, domain: a.domain, tag: a.brk, place, holdMs: a.holdMs, fillMs: cold ? Math.max(a.fillMs, COLD_FILL_MS) : a.fillMs,
    go: () => {
      if (done) return;
      done = true;
      adBreak.value = null;
      start();
    },
  };
}

// ───────────────────────── پایانِ میز ─────────────────────────

/**
 * میزِ جاری از اتاقِ دوستان آمده؟ اتاق آگهی ندارد — نه پیش از میز و نه روی پنلِ پایانش
 * (همان قاعده‌ی اپ). با دیدنِ اتاق روشن و با هر شروع از منو ({@link beforeTable}) خاموش.
 */
let viaRoom = false;

/** بازیکن در اتاقِ دوستان است — میزی که از آن شروع شود «میزِ اتاق» است. */
export function noteRoom(): void { viaRoom = true; }

/** ویجتِ پنلِ پایانِ میز برای همین میز؛ خالی = بی آگهی. */
export function resultTag(subscriber: boolean, a: WebAds | null = ads.value): string {
  if (!a || !a.result || subscriber || viaRoom) return "";
  return a.result;
}

const noted = new Set<string>();

/**
 * نتیجه‌ی یک مسابقه برای «باختِ پشتِ هم» — یک بار برای هر میز (پنل ممکن است دوباره
 * رندر یا سوار شود). تماشاچی نمی‌بازد.
 */
export function noteMatchEnd(key: string, won: boolean | null, watching: boolean): void {
  if (watching || !key || noted.has(key)) return;
  noted.add(key);
  book.noteResult(won);
}

/**
 * پس از رسیدنِ پیکربندی: تنظیم را بنشان و لودر را زود بیاور، تا وقتی بازیکن
 * «بازیِ سریع» را زد آماده باشد. مشترک بودن را این‌جا هنوز نمی‌دانیم (از سوکت
 * می‌آید)؛ لودرِ بی ویجت چیزی نشان نمی‌دهد و مشترک هیچ ویجتی نمی‌گیرد.
 */
export function initWebAds(): void {
  const a = readAds(json<AdsKey | null>("ads", null));
  ads.value = a;
  if (!a) return;
  book.touch();
  ensureLoader(a.domain);
}

/** فقط برای آزمون. */
export function __resetAds(): void {
  ads.value = null;
  adBreak.value = null;
  warm = null;
  viaRoom = false;
  noted.clear();
  queue = [];
  if (flushT !== null) { clearTimeout(flushT); flushT = null; }
}
